import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Payment, PaymentCreate, PaymentUpdate } from '@/types/payment';
import api from '../config/axios';
import { notifyEntityCreated, notifyEntityUpdated } from '../utils/entityNotifications';
import { STATUS_NAMES, STATUS_TYPES } from '../constants/statusConstants';
import { useStatusStore } from './statusStore';

// Note: Payment API endpoints would need to be defined in apiPaths.ts
import { API_BASE } from '../config/apiPaths';

const PAYMENTS_API = {
  ALL: `${API_BASE}/payments`,
  BY_ID: (id: number) => `${API_BASE}/payments/${id}`,
  BY_INVOICE: (invoiceId: number) => `${API_BASE}/payments/invoice/${invoiceId}`,
};

interface PaymentState {
  payments: Payment[];
  currentPayment: Payment | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchPayments: (organizationId?: number) => Promise<void>;
  fetchPaymentsByInvoice: (invoiceId: number) => Promise<Payment[]>;
  getPayment: (id: number) => Promise<Payment | undefined>;
  createPayment: (payment: PaymentCreate) => Promise<Payment>;
  updatePayment: (id: number, updates: PaymentUpdate) => Promise<Payment>;
  deletePayment: (id: number) => Promise<void>;
  
  // Helpers
  setCurrentPayment: (payment: Payment | null) => void;
  clearCurrentPayment: () => void;
  getConfirmedPaymentsTotal: (invoiceId: number) => number;
}

const usePaymentStore = create<PaymentState>()(
  devtools(
    (set, get) => ({
      payments: [],
      currentPayment: null,
      loading: false,
      error: null,

      fetchPayments: async (organizationId?: number) => {
        set({ loading: true, error: null });
        try {
          const params = organizationId ? { organization_id: organizationId } : {};
          const response = await api.get<Payment[]>(PAYMENTS_API.ALL, { params });
          set({ payments: response.data, loading: false });
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch payments',
            loading: false 
          });
        }
      },

      fetchPaymentsByInvoice: async (invoiceId: number) => {
        set({ loading: true, error: null });
        try {
          const response = await api.get<Payment[]>(PAYMENTS_API.BY_INVOICE(invoiceId));
          const invoicePayments = response.data;
          
          // Update the main payments list with these payments
          const currentPayments = get().payments;
          const otherPayments = currentPayments.filter(p => p.invoice_id !== invoiceId);
          const updatedPayments = [...otherPayments, ...invoicePayments];
          
          set({ payments: updatedPayments, loading: false });
          return invoicePayments;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch invoice payments',
            loading: false 
          });
          return [];
        }
      },

      getPayment: async (id: number) => {
        set({ loading: true, error: null });
        try {
          const response = await api.get<Payment>(PAYMENTS_API.BY_ID(id));
          const payment = response.data;
          set({ currentPayment: payment, loading: false });
          return payment;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch payment',
            loading: false 
          });
          return undefined;
        }
      },

      createPayment: async (payment: PaymentCreate) => {
        set({ loading: true, error: null });
        try {
          const response = await api.post<Payment>(PAYMENTS_API.ALL, payment);
          const newPayment = response.data;
          
          set(state => ({ 
            payments: [...state.payments, newPayment], 
            loading: false 
          }));
          
          notifyEntityCreated('payment', {
            id: newPayment.id ?? 'New',
            title: newPayment.reference_number || (newPayment.id ? `Payment #${newPayment.id}` : 'New Payment')
          });
          return newPayment;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to create payment',
            loading: false 
          });
          throw error;
        }
      },

      updatePayment: async (id: number, updates: PaymentUpdate) => {
        set({ loading: true, error: null });
        try {
          const response = await api.put<Payment>(PAYMENTS_API.BY_ID(id), updates);
          const updatedPayment = response.data;
          
          set(state => ({
            payments: state.payments.map(p => p.id === id ? updatedPayment : p),
            currentPayment: state.currentPayment?.id === id ? updatedPayment : state.currentPayment,
            loading: false
          }));
          
          notifyEntityUpdated('payment', {
            id: updatedPayment.id ?? id,
            title: updatedPayment.reference_number || (updatedPayment.id ? `Payment #${updatedPayment.id}` : `Payment #${id}`)
          });
          return updatedPayment;
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to update payment',
            loading: false 
          });
          throw error;
        }
      },

      deletePayment: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await api.delete(PAYMENTS_API.BY_ID(id));
          
          set(state => ({
            payments: state.payments.filter(p => p.id !== id),
            currentPayment: state.currentPayment?.id === id ? null : state.currentPayment,
            loading: false
          }));
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to delete payment',
            loading: false 
          });
          throw error;
        }
      },

      setCurrentPayment: (payment: Payment | null) => {
        set({ currentPayment: payment });
      },

      clearCurrentPayment: () => {
        set({ currentPayment: null });
      },

      getConfirmedPaymentsTotal: (invoiceId: number) => {
        const { payments } = get();
        const validatedId = useStatusStore.getState().getStatusIdByNameAndType(
          STATUS_NAMES.VALIDATED,
          STATUS_TYPES.PAYMENT
        );
        if (!validatedId) {
          // Status list not loaded yet — do not silently treat every payment as confirmed.
          return 0;
        }
        return payments
          .filter(p => p.invoice_id === invoiceId && p.status_id === validatedId)
          .reduce((total, p) => total + p.amount, 0);
      },
    }),
    {
      name: 'payment-store',
    }
  )
);

export { usePaymentStore };
export default usePaymentStore;